import { Severity } from 'Data/ViewModels/Core/types';
import { DateTimeDataFormat, FormatSpecifier, getTimeInputDataForPattern } from 'Lib/Format';
import classNames from 'classnames';
import moment from 'moment';
import React, { useState } from 'react';
import { withInjectedProps } from '../InjectedPropsContext';

const minValidDate = '1800-01-01';
const maxValidDate = '9999-12-31';

export type IDateInputProps = Pick<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'defaultValue' | 'disabled' | 'id' | 'name' | 'onChange' | 'onKeyDown'
> & {
  focusRef?: React.Ref<HTMLInputElement>;
  severity?: Severity;
  value?: any;
  required?: boolean;
  dataFormat?: DateTimeDataFormat;
  format?: FormatSpecifier;
  min?: string;
  max?: string;
};

const DateInputBase: React.FC<IDateInputProps> = ({
  defaultValue,
  disabled,
  id,
  focusRef,
  name,
  onChange,
  onKeyDown,
  severity,
  value,
  required,
  dataFormat = DateTimeDataFormat.Date,
  format,
  min,
  max,
}) => {
  //When we have linked date and time inputs, we need better control over what is written back to the underlying model
  //state. This is essentially to delay clearing the underlying value when the input value is null due to the entered
  //date being invalid.
  const [inputValue, setInputValue] = useState<any>();
  const [dirty, setDirty] = useState<boolean>(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      invokeOnChange(event, true);
    }
    onKeyDown?.(event);
  };
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => invokeOnChange(event, true);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => invokeOnChange(event, false);

  const invokeOnChange = (event: any, submitValues: boolean) => {
    let date = event.target.value?.length > 0 ? moment(event.target.value) : null;

    // In some browsers (Firefox), clicking the clear 'x' doesn't focus or blur the input, so
    // if if the value was cleared, and we don't currently have focus on the input, submit the
    // change immediately.
    const submitValuesBecauseCleared =
      date === null && event.target !== document.activeElement && (event.target as HTMLInputElement).checkValidity();

    // Delay writing to the model if this is part of a pair of linked date and time inputs, and value is only
    // temporarily cleared (we always need to write to the model before submitting values to the server).
    // also delay writing to the model in the case where the value is before 1800 to avoid accidentally trying
    // to sync a value from the model that can't be represented in an SQL datetime value.
    if (
      ((dataFormat === DateTimeDataFormat.UnspecifiedLocalDateTime && date === null) ||
        date?.isBefore(minValidDate) ||
        date?.isAfter(maxValidDate)) &&
      !(submitValues || submitValuesBecauseCleared)
    ) {
      setInputValue(event.target.value);
      setDirty(true);
      return;
    }

    // Hack to prevent errors from submitting dates that can't be represented. Should be done server-side really.
    if (submitValues && date && date.isBefore(minValidDate)) {
      date = date.set('year', 1800);
    }
    if (submitValues && date && date.isAfter(maxValidDate)) {
      date = date.set('year', 9999);
    }

    var newValue = date?.format(moment.HTML5_FMT.DATE) ?? null;
    setInputValue(newValue);

    if (dataFormat === DateTimeDataFormat.UnspecifiedLocalDateTime) {
      // The value on the model is an "unspecified local date and time". When value in the date input is set, we want to
      // modify only the date part of the value, and leave the time of day intact.

      // When the date is changed from `null` to a value, we need to set the time of day so that the underlying value is
      // a full date/time. Rather than defaulting to 'midnight', we default to "now", the idea being that the usual use
      // case is "today" -> "now", and, we want the precision to match the precision used for the linked time input.
      const formatInfo = getTimeInputDataForPattern(format?.pattern ?? moment.HTML5_FMT.TIME);
      var oldDateTime = moment(value, 'YYYY-MM-DDTHH:mm:ss.SSS', true);
      if (!oldDateTime.isValid()) {
        if (formatInfo.includeFractionalSeconds) {
          oldDateTime = moment().local();
        } else if (formatInfo.includeSeconds) {
          oldDateTime = moment().local().millisecond(0);
        } else {
          oldDateTime = moment().local().second(0).millisecond(0);
        }
      }
      var newDate = moment(newValue, moment.HTML5_FMT.DATE, true);
      if (newDate.isValid()) {
        newValue = oldDateTime
          .year(newDate.year())
          .month(newDate.month())
          .date(newDate.date())
          .format('YYYY-MM-DDTHH:mm:ss.SSS');
      }
    }
    if (newValue == null && (submitValues || submitValuesBecauseCleared)) {
      //Clear partially entered value to avoid possible confusion with a value being displayed, but not being saved
      event.target.value = '';
    }
    event.data = { name, value: newValue, submitValues: submitValues || submitValuesBecauseCleared };

    onChange?.(event);
    setDirty(false);
  };

  const getRef = (instance: HTMLInputElement | null) => {
    // QP-2283 - The clear button in iOS date picker sets the value to the default value.
    // The following is needed to set it to empty string
    // so that it clears - https://github.com/facebook/react/issues/8938
    if (instance) instance.defaultValue = '';
    if (typeof focusRef === 'object' && focusRef) (focusRef as any).current = instance;
  };

  // Undefined needs to pass through to switch between a controlled vs uncontrolled component.
  // When this is a linked date/time input, we are extracting only the date portion of the underlying
  // value on the model.
  var inputValueFromModel =
    value === undefined || value === null
      ? value
      : moment(value, ['YYYY-MM-DDTHH:mm:ss.SSS', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD']).format(moment.HTML5_FMT.DATE);

  // Sync value to display from props unless user has made local changes
  // Local changes flagged through handleChange and then synced through handleBlur
  if (value !== undefined && !dirty && inputValue !== inputValueFromModel) {
    setInputValue(inputValueFromModel);
  }

  const cx = classNames('DateInput', { [`Input-${severity}`]: !!severity });

  //Converts a full datetime string to a date format.
  //The min property is required to be in the format of yyyy-MM-dd or yyyy-MM-ddThh:mm
  const convertFromDateTime = (d?: string) => {
    if (!d) return undefined;

    // Parse the input string into a Date object
    const date = new Date(d);

    // Extract year, month, and day from the Date object
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is zero-indexed
    const day = String(date.getDate()).padStart(2, '0');

    // Return date as a string in the format of yyyy-MM-dd
    return `${year}-${month}-${day}`;
  };

  min = convertFromDateTime(min);

  return (
    <input
      className={cx}
      data-testid={id}
      defaultValue={defaultValue === undefined ? undefined : defaultValue ?? ''}
      disabled={disabled}
      id={id}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      ref={getRef}
      type="date"
      value={value === undefined ? undefined : inputValue ?? ''}
      required={required}
      min={min ?? '1800-01-01'}
      max={max ?? '9999-12-31'}
    />
  );
};

const DateInput = withInjectedProps(DateInputBase);

export { DateInput, DateInputBase };
export default DateInput;
